/*
 * Edit rules and Alpaca provision errors for US corporate applications.
 *
 * Company details can be edited until the company (legal entity) exists on
 * Alpaca, i.e. until legalEntityPartyId is set, and only while the trading
 * account has not been created.
 *
 * Representatives (parties) can be edited until the trading account is
 * created, even after approval and even when legalEntityPartyId is set.
 *
 * The trading account counts as created when any of these is true:
 *   status is ACCOUNT_CREATED, or alpacaAccountId / tradingAccountId /
 *   accountCreatedAt has a value.
 *
 * The helpers accept either the full GET /applications/{id} response
 * ({ application, legalEntity, alpacaBta, ... }) or a single list row.
 */

const hasValue = (value) => value !== undefined && value !== null && `${value}`.trim() !== "";

const sourcesOf = (data) => [
    data,
    data?.application,
    data?.legalEntity,
    data?.alpacaBta,
    data?.application?.alpacaBta,
].filter((source) => source && typeof source === "object");

const firstValue = (data, key) => {
    for (const source of sourcesOf(data)) {
        if (hasValue(source[key])) return source[key];
    }
    return null;
};

export const getApplicationStatus = (data) => {
    for (const source of sourcesOf(data)) {
        for (const key of ["applicationStatus", "status"]) {
            // Skip non-string values such as an API envelope's `status: true`.
            if (typeof source[key] === "string" && source[key].trim()) {
                return source[key].trim().toUpperCase();
            }
        }
    }
    return "";
};

export const getLegalEntityPartyId = (data) => firstValue(data, "legalEntityPartyId");

export const isTradingAccountCreated = (data) => (
    getApplicationStatus(data) === "ACCOUNT_CREATED"
    || hasValue(firstValue(data, "alpacaAccountId"))
    || hasValue(firstValue(data, "tradingAccountId"))
    || hasValue(firstValue(data, "accountCreatedAt"))
);

export const canEditCompany = (data) => !isTradingAccountCreated(data) && !hasValue(getLegalEntityPartyId(data));

export const canEditReps = (data) => !isTradingAccountCreated(data);

export const companyLockReason = (data) => {
    if (isTradingAccountCreated(data)) return "The trading account has been created, so company details can no longer be changed.";
    if (hasValue(getLegalEntityPartyId(data))) return "The company has been created on Alpaca, so company details can no longer be changed.";
    return "";
};

export const repsLockReason = (data) => (
    isTradingAccountCreated(data)
        ? "The trading account has been created, so representatives can no longer be changed."
        : ""
);

/* Reads checks.alpacaProvision from the eligibility response
 * (GET usPortfolioEligibility?userType=CORPORATE&applicationId=...). */
export const getAlpacaProvisionError = (eligibility) => {
    const provision = eligibility?.checks?.alpacaProvision || {};
    if (provision?.blocked !== true) return null;
    const lastError = provision?.lastError || {};
    return {
        message: provision?.blockedReason || eligibility?.blockedReason || "Alpaca provisioning failed.",
        step: lastError?.step || "",
        cause: lastError?.cause || "",
        legalEntityPartyCreated: Boolean(provision?.legalEntityPartyCreated),
    };
};

export const ProvisionErrorAlert = ({ eligibility, className = "mb-4" }) => {
    const error = getAlpacaProvisionError(eligibility);
    if (!error) return null;
    return (
        <div className={`alert alert-light-danger ${className}`}>
            <div className="font-weight-bolder mb-1">Alpaca provisioning failed</div>
            <div>{error.message}</div>
            {(error.step || error.cause) && (
                <div className="font-size-sm text-muted mt-2">
                    {error.step && <span className="mr-3">Step: <code>{error.step}</code></span>}
                    {error.cause && <span>Cause: {error.cause}</span>}
                </div>
            )}
        </div>
    );
};

export const EditLockAlert = ({ reason, className = "mb-4" }) => (
    reason ? <div className={`alert alert-light-warning ${className}`}>{reason}</div> : null
);
